// @ts-nocheck
"use client";
import * as React from "react";
import markdownIt from "markdown-it";
import moment from "moment-timezone";
import * as antd from 'antd';
import * as antv from "@antv/g6";
// import * as antdStyle from 'antd-style';
import * as antdPlots from '@ant-design/plots';
import * as antdIcons from '@ant-design/icons';

// @ts-nocheck
"use client";
const {
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
    createContext,
    useContext,
} = React;

class BaseMessage {
    constructor(fields) {
        if (typeof fields === "string") {
            this.content = fields;
            this.kwargs = {};
        } else {
            this.content = fields.content || "";
            this.id = fields.id;
            this.kwargs = fields.kwargs || fields;
        }
    }
}
class HumanMessage extends BaseMessage {
    constructor(fields) {
        super(fields);
        this.type = "human";
    }
    static isInstance(message) {
        return message?.type === "human";
    }
}
class AIMessage extends BaseMessage {
    constructor(fields) {
        super(fields);
        this.type = "ai";
    }
    static isInstance(message) {
        return message?.type === "ai";
    }
}
// ================================================
// Part: constants
// ================================================
const EVENT_TYPES = [
    "update",
    "interrupt",
    "agent",
    "agent_state",
    "model_request",
    "tools",
    "end",
    "error",
];
// ================================================
// Part: statistics
// ================================================
/**
 * Default token counter that approximates based on character count
 * Handles both BaseMessage format and model_request message format
 * @param messages Messages to count tokens for (can be BaseMessage[] or model_request message format)
 * @returns Approximate token count
 */
function countTokensApproximately(messages) {
    let totalChars = 0;
    for (const msg of messages) {
        let textContent = "";
        // Handle BaseMessage format and LangGraph native format (has content property directly)
        if ("content" in msg) {
            const content = msg.content;
            if (typeof content === "string") {
                textContent = content;
            } else if (Array.isArray(content)) {
                textContent = content
                    .map((item) => {
                        if (typeof item === "string") return item;
                        if (
                            item &&
                            typeof item === "object" &&
                            "type" in item &&
                            item.type === "text" &&
                            "text" in item
                        ) {
                            return item.text;
                        }
                        return "";
                    })
                    .join("");
            }
        }
        totalChars += textContent.length;
    }
    // Approximate 1 token = 4 characters
    return Math.ceil(totalChars / 4);
}
const { Input, Button, Layout, Spin, Typography, Menu, Alert, Upload, Modal, Space, Popover } = antd || {};
const { TextArea } = Input || {};
const { Text } = Typography || {};
const {
    ArrowUpOutlined,
    StopOutlined,
    HistoryOutlined,
    SettingOutlined,
    CloseOutlined,
    PlusOutlined,
    DeleteOutlined,
    MessageOutlined,
    UploadOutlined,
    FileTextOutlined,
    ReloadOutlined
} = antdIcons || {};
const StatisticsContext = createContext(undefined);
function StatisticsProvider({ children }) {
    const [statistics, setStatistics] = useState({
        toolCalls: new Map(),
        modelCalls: 0,
        tokens: {
            input: 0,
            output: 0,
            total: 0,
        },
        contextWindowSize: 0,
    });
    const recordToolCall = useCallback((toolName) => {
        setStatistics((prev) => {
            const newToolCalls = new Map(prev.toolCalls);
            const currentCount = newToolCalls.get(toolName) || 0;
            newToolCalls.set(toolName, currentCount + 1);
            return {
                ...prev,
                toolCalls: newToolCalls,
            };
        });
    }, []);
    const recordModelCall = useCallback(() => {
        setStatistics((prev) => ({
            ...prev,
            modelCalls: prev.modelCalls + 1,
        }));
    }, []);
    const recordTokens = useCallback((input, output, total) => {
        setStatistics((prev) => ({
            ...prev,
            tokens: {
                input: prev.tokens.input + input,
                output: prev.tokens.output + output,
                total: prev.tokens.total + total,
            },
        }));
    }, []);
    const recordContextWindowSize = useCallback((contextWindowSize) => {
        setStatistics((prev) => ({
            ...prev,
            contextWindowSize,
        }));
    }, []);
    const resetStatistics = useCallback(() => {
        setStatistics({
            toolCalls: new Map(),
            modelCalls: 0,
            tokens: {
                input: 0,
                output: 0,
                total: 0,
            },
            contextWindowSize: 0,
        });
    }, []);
    return (
        <StatisticsContext.Provider
            value={{
                statistics,
                recordToolCall,
                recordModelCall,
                recordTokens,
                recordContextWindowSize,
                resetStatistics,
            }}
        >
            {children}
        </StatisticsContext.Provider>
    );
}
function useStatistics() {
    const context = useContext(StatisticsContext);
    if (context === undefined) {
        throw new Error("useStatistics must be used within a StatisticsProvider");
    }
    return context;
}

function ToolCallBubble({ toolCallState }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const getToolMessageInfo = (toolMessage) => {
        const msgAny = toolMessage;
        if ("status" in msgAny && "content" in msgAny) {
            return {
                status: msgAny.status,
                content: msgAny.content,
            };
        }
        if (msgAny.kwargs && typeof msgAny.kwargs === "object") {
            const kwargs = msgAny.kwargs;
            return {
                status: kwargs.status,
                content: kwargs.content,
            };
        }
        return { status: undefined, content: undefined };
    };
    const toolMessageInfo = toolCallState.toolMessage
        ? getToolMessageInfo(toolCallState.toolMessage)
        : null;
    const isErrored =
        toolCallState.errored ||
        (toolMessageInfo && toolMessageInfo.status === "error");
    const borderColor = isErrored ? "#ff4d4f" : "#424242";
    const bgColor = isErrored ? "#2a1215" : "#1f1f1f";
    const hasResult = !!toolCallState.toolMessage;
    const isWaiting = !toolCallState.toolMessage && !isErrored;

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "flex-start",
                marginTop: "8px",
            }}
        >
            <div
                style={{
                    maxWidth: "80%",
                    borderRadius: "8px",
                    border: "1px solid " + borderColor,
                    backgroundColor: bgColor,
                    overflow: "hidden",
                }}
            >
                {/* Header — always visible, clickable to toggle */}
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "8px 12px",
                        textAlign: "left",
                        backgroundColor: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: "#ececec",
                    }}
                >
                    {/* Chevron */}
                    <span
                        style={{
                            display: "inline-block",
                            transition: "transform 0.2s",
                            transform: isExpanded ? "rotate(90deg)" : "none",
                            fontSize: "12px",
                            color: "#888",
                        }}
                    >
                        ▶
                    </span>

                    <span
                        style={{
                            fontSize: "10px",
                            fontWeight: "600",
                            color: "#888",
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                        }}
                    >
                        Tool
                    </span>
                    <span
                        style={{
                            fontSize: "12px",
                            fontFamily: "monospace",
                            fontWeight: "600",
                        }}
                    >
                        {toolCallState.toolCall.name}
                    </span>

                    {/* Status indicators */}
                    {isErrored && (
                        <span
                            style={{
                                fontSize: "10px",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                backgroundColor: "#4a1c1c",
                                color: "#ff4d4f",
                                fontWeight: "500",
                            }}
                        >
                            Error
                        </span>
                    )}
                    {hasResult && !isErrored && toolMessageInfo?.status === "success" && (
                        <span
                            style={{
                                fontSize: "10px",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                backgroundColor: "#1c3c24",
                                color: "#52c41a",
                                fontWeight: "500",
                            }}
                        >
                            ✓
                        </span>
                    )}
                    {isWaiting && <Spin size="small" style={{ marginLeft: "4px" }} />}
                </button>

                {/* Collapsible body */}
                {isExpanded && (
                    <div style={{ borderTop: "1px solid #424242", padding: "8px 12px" }}>
                        {/* Arguments */}
                        <div style={{ marginBottom: "8px" }}>
                            <div
                                style={{
                                    fontSize: "10px",
                                    fontWeight: "600",
                                    color: "#888",
                                    textTransform: "uppercase",
                                    letterSpacing: "1px",
                                    marginBottom: "4px",
                                }}
                            >
                                Args
                            </div>
                            <div
                                style={{
                                    backgroundColor: "#141414",
                                    borderRadius: "4px",
                                    padding: "8px",
                                }}
                            >
                                <pre
                                    style={{
                                        fontSize: "12px",
                                        color: "#ececec",
                                        overflowX: "auto",
                                        margin: 0,
                                        backgroundColor: "transparent",
                                    }}
                                >
                                    {JSON.stringify(toolCallState.toolCall.args, null, 2)}
                                </pre>
                            </div>
                        </div>

                        {/* Result */}
                        {toolCallState.toolMessage && toolMessageInfo && (
                            <div>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        marginBottom: "4px",
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: "10px",
                                            fontWeight: "600",
                                            color: "#888",
                                            textTransform: "uppercase",
                                            letterSpacing: "1px",
                                        }}
                                    >
                                        Result
                                    </span>
                                    {toolMessageInfo.status && (
                                        <span
                                            style={{
                                                fontSize: "10px",
                                                padding: "2px 6px",
                                                borderRadius: "4px",
                                                fontWeight: "500",
                                                backgroundColor:
                                                    toolMessageInfo.status === "success"
                                                        ? "#1c3c24"
                                                        : "#4a1c1c",
                                                color:
                                                    toolMessageInfo.status === "success"
                                                        ? "#52c41a"
                                                        : "#ff4d4f",
                                            }}
                                        >
                                            {toolMessageInfo.status}
                                        </span>
                                    )}
                                </div>
                                <div
                                    style={{
                                        backgroundColor: "#141414",
                                        borderRadius: "4px",
                                        padding: "8px",
                                    }}
                                >
                                    <pre
                                        style={{
                                            fontSize: "12px",
                                            color: "#ececec",
                                            overflowX: "auto",
                                            whiteSpace: "pre-wrap",
                                            maxHeight: "192px",
                                            margin: 0,
                                            backgroundColor: "transparent",
                                        }}
                                    >
                                        {toolMessageInfo.content || ""}
                                    </pre>
                                </div>
                            </div>
                        )}

                        {/* Waiting state */}
                        {isWaiting && (
                            <div
                                style={{
                                    fontSize: "12px",
                                    color: "#888",
                                    fontStyle: "italic",
                                    marginTop: "8px",
                                }}
                            >
                                Waiting for result...
                            </div>
                        )}

                        {/* Error without result */}
                        {isErrored && !toolCallState.toolMessage && (
                            <div
                                style={{
                                    fontSize: "12px",
                                    color: "#ff4d4f",
                                    fontStyle: "italic",
                                    marginTop: "8px",
                                }}
                            >
                                Tool call failed due to an error
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
function InterruptBubble({ interruptData, onApprove, onReject, onEdit }) {
    const [rejectionMessage, setRejectionMessage] = useState("");
    const [editingActionIndex, setEditingActionIndex] = useState(null);
    const [editedArgs, setEditedArgs] = useState({});
    const [showRejectionInput, setShowRejectionInput] = useState(false);
    // Validate interrupt data
    if (
        !interruptData ||
        !Array.isArray(interruptData.actionRequests) ||
        interruptData.actionRequests.length === 0
    ) {
        return null;
    }
    const actionRequest = interruptData.actionRequests[0]; // Handle first action request
    const reviewConfig = interruptData.reviewConfigs?.find(
        (config) => config.actionName === actionRequest.name,
    );
    const allowedDecisions = reviewConfig?.allowedDecisions || [
        "approve",
        "reject",
    ];
    const handleApprove = () => {
        onApprove();
    };
    const handleReject = () => {
        if (showRejectionInput) {
            onReject(rejectionMessage.trim() || undefined);
        } else {
            setShowRejectionInput(true);
        }
    };
    const handleRejectConfirm = () => {
        onReject(rejectionMessage.trim() || undefined);
    };
    const handleEdit = () => {
        if (editingActionIndex !== null) {
            onEdit(editedArgs);
            setEditingActionIndex(null);
            setEditedArgs({});
        }
    };
    const startEditing = () => {
        setEditingActionIndex(0);
        setEditedArgs(JSON.parse(JSON.stringify(actionRequest.args)));
        setShowRejectionInput(false);
    };
    const cancelEditing = () => {
        setEditingActionIndex(null);
        setEditedArgs({});
    };
    const updateEditedArg = (key, value) => {
        try {
            // Try to parse as JSON if it looks like JSON
            const parsedValue =
                value.trim().startsWith("{") || value.trim().startsWith("[")
                    ? JSON.parse(value)
                    : value;
            setEditedArgs((prev) => ({ ...prev, [key]: parsedValue }));
        } catch {
            // If parsing fails, use as string
            setEditedArgs((prev) => ({ ...prev, [key]: value }));
        }
    };
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "flex-start",
                marginTop: "16px",
            }}
        >
            <div
                style={{
                    maxWidth: "80%",
                    borderRadius: "8px",
                    border: "2px solid #ca8a04",
                    backgroundColor: "rgba(113, 63, 18, 0.2)",
                    padding: "16px",
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {/* Header */}
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span
                            style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "#fde047",
                                textTransform: "uppercase",
                            }}
                        >
                            ⚠️ Approval Required
                        </span>
                    </div>

                    {/* Description */}
                    <div>
                        <p style={{ fontSize: "14px", color: "#d1d5db", margin: 0 }}>
                            {actionRequest.description}
                        </p>
                    </div>

                    {/* Tool Details */}
                    <div
                        style={{
                            backgroundColor: "#1f2937",
                            borderRadius: "8px",
                            padding: "16px",
                            border: "1px solid #374151",
                        }}
                    >
                        <div style={{ marginBottom: "12px" }}>
                            <span
                                style={{
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    color: "#9ca3af",
                                    textTransform: "uppercase",
                                }}
                            >
                                Tool Name
                            </span>
                            <p
                                style={{
                                    fontSize: "14px",
                                    fontFamily: "monospace",
                                    fontWeight: "600",
                                    color: "#f3f4f6",
                                    margin: "4px 0 0 0",
                                }}
                            >
                                {actionRequest.name}
                            </p>
                        </div>

                        {editingActionIndex === null ? (
                            <div>
                                <span
                                    style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        color: "#9ca3af",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Arguments
                                </span>
                                <pre
                                    style={{
                                        fontSize: "12px",
                                        color: "#d1d5db",
                                        overflowX: "auto",
                                        marginTop: "8px",
                                        padding: "12px",
                                        backgroundColor: "#111827",
                                        borderRadius: "4px",
                                        border: "1px solid #374151",
                                    }}
                                >
                                    {JSON.stringify(actionRequest.args, null, 2)}
                                </pre>
                            </div>
                        ) : (
                            <div>
                                <span
                                    style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        color: "#9ca3af",
                                        textTransform: "uppercase",
                                        marginBottom: "8px",
                                        display: "block",
                                    }}
                                >
                                    Edit Arguments
                                </span>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "12px",
                                        marginTop: "8px",
                                    }}
                                >
                                    {Object.entries(actionRequest.args).map(([key, value]) => (
                                        <div key={key}>
                                            <label
                                                style={{
                                                    display: "block",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                    color: "#d1d5db",
                                                    marginBottom: "4px",
                                                }}
                                            >
                                                {key}
                                            </label>
                                            <textarea
                                                value={
                                                    editedArgs[key] !== undefined
                                                        ? typeof editedArgs[key] === "string"
                                                            ? editedArgs[key]
                                                            : JSON.stringify(editedArgs[key], null, 2)
                                                        : typeof value === "string"
                                                            ? value
                                                            : JSON.stringify(value, null, 2)
                                                }
                                                onChange={(e) => updateEditedArg(key, e.target.value)}
                                                style={{
                                                    width: "100%",
                                                    padding: "8px 12px",
                                                    fontSize: "12px",
                                                    border: "1px solid #4b5563",
                                                    borderRadius: "4px",
                                                    backgroundColor: "#111827",
                                                    color: "#f3f4f6",
                                                    fontFamily: "monospace",
                                                    boxSizing: "border-box",
                                                }}
                                                rows={
                                                    typeof value === "string" && value.length < 100
                                                        ? 2
                                                        : 6
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Rejection Message Input */}
                    {showRejectionInput && editingActionIndex === null && (
                        <div>
                            <label
                                style={{
                                    display: "block",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#d1d5db",
                                    marginBottom: "8px",
                                }}
                            >
                                Rejection Message (optional)
                            </label>
                            <textarea
                                value={rejectionMessage}
                                onChange={(e) => setRejectionMessage(e.target.value)}
                                placeholder="Explain why you're rejecting this action..."
                                style={{
                                    width: "100%",
                                    padding: "8px 12px",
                                    border: "1px solid #4b5563",
                                    borderRadius: "4px",
                                    backgroundColor: "#1f2937",
                                    color: "#f3f4f6",
                                    fontSize: "14px",
                                    boxSizing: "border-box",
                                }}
                                rows={3}
                            />
                        </div>
                    )}

                    {/* Actions */}
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "12px",
                            paddingTop: "8px",
                        }}
                    >
                        {editingActionIndex !== null ? (
                            <>
                                <button
                                    onClick={cancelEditing}
                                    style={{
                                        padding: "8px 16px",
                                        border: "1px solid #4b5563",
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        color: "#d1d5db",
                                        backgroundColor: "transparent",
                                        cursor: "pointer",
                                    }}
                                >
                                    Cancel Edit
                                </button>
                                <button
                                    onClick={handleEdit}
                                    style={{
                                        padding: "8px 16px",
                                        backgroundColor: "#2563eb",
                                        border: "none",
                                        color: "white",
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        cursor: "pointer",
                                    }}
                                >
                                    Save & Approve
                                </button>
                            </>
                        ) : showRejectionInput ? (
                            <>
                                <button
                                    onClick={() => {
                                        setShowRejectionInput(false);
                                        setRejectionMessage("");
                                    }}
                                    style={{
                                        padding: "8px 16px",
                                        border: "1px solid #4b5563",
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        color: "#d1d5db",
                                        backgroundColor: "transparent",
                                        cursor: "pointer",
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleRejectConfirm}
                                    style={{
                                        padding: "8px 16px",
                                        backgroundColor: "#dc2626",
                                        border: "none",
                                        color: "white",
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        cursor: "pointer",
                                    }}
                                >
                                    Confirm Reject
                                </button>
                            </>
                        ) : (
                            <>
                                {allowedDecisions.includes("reject") && (
                                    <button
                                        onClick={handleReject}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: "#dc2626",
                                            border: "none",
                                            color: "white",
                                            borderRadius: "8px",
                                            fontSize: "14px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Reject
                                    </button>
                                )}
                                {allowedDecisions.includes("edit") && (
                                    <button
                                        onClick={startEditing}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: "#ca8a04",
                                            border: "none",
                                            color: "white",
                                            borderRadius: "8px",
                                            fontSize: "14px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Edit
                                    </button>
                                )}
                                {allowedDecisions.includes("approve") && (
                                    <button
                                        onClick={handleApprove}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: "#16a34a",
                                            border: "none",
                                            color: "white",
                                            borderRadius: "8px",
                                            fontSize: "14px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Approve
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
// Regex patterns using constructor to avoid TSX angle bracket issues
const THINK_REGEX = new RegExp("<think>[\\s\\S]*?</think>", "g");
const PLAN_OPEN = new RegExp("<PLAN>", "g");
const PLAN_CLOSE = new RegExp("</PLAN>", "g");
function cleanContent(raw) {
    let text = raw;
    // Strip think blocks entirely
    text = text.replace(THINK_REGEX, "");
    // Convert PLAN tags to blockquote
    text = text.replace(PLAN_OPEN, "\n> **🗂 Plan**\n> \n> ");
    text = text.replace(PLAN_CLOSE, "\n");
    // Fix incomplete code fences during streaming:
    // Count triple backtick occurrences — if odd, add a closing fence
    const fenceMatches = text.match(/```/g);
    if (fenceMatches && fenceMatches.length % 2 !== 0) {
        text += "\n```";
    }
    return text.trim();
}
// Markdown Configuration (Global)
const markdown =
    typeof markdownIt !== "undefined"
        ? markdownIt({
            html: false,
            typographer: true,
            highlight: function (code, lang = "javascript") {
                lang = "javascript";
                const uuid =
                    typeof uuidv4 !== "undefined"
                        ? uuidv4()
                        : Math.random().toString(36).substring(7);
                if (lang && typeof hljs !== "undefined" && hljs.getLanguage(lang)) {
                    try {
                        return `<pre style="background-color: #2d2d2d;color: #f0f0f0;padding: 10px;border-radius: 5px;" id="codeBlock">${hljs.highlight(code, { language: lang, ignoreIllegals: true }).value}</pre>`;
                    } catch (__) { }
                }

                const encodedCode =
                    typeof HTMLEncode !== "undefined"
                        ? HTMLEncode(code, "javascript")
                        : code
                            .replace(/&/g, "&amp;")
                            .replace(/</g, "&lt;")
                            .replace(/>/g, "&gt;")
                            .replace(/"/g, "&quot;")
                            .replace(/'/g, "&#039;");
                return (
                    `<div class="whitespace-pre-line w-full rounded-lg bg-black-900 px-4 pb-4 relative font-mono font-normal text-sm text-slate-200">
        <div class="w-full flex items-center absolute top-0 left-0 text-slate-200 bg-stone-800 px-4 py-2 text-xs font-sans justify-between rounded-t-md">
          <div class="flex gap-2"><code class="text-xs"></code></div>
          <button data-code-snippet data-code="code-${uuid}" class="flex items-center gap-x-2">
            <svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>
            <p>Copy code</p>
          </button>
        </div>
      <pre class="whitespace-pre-wrap">` +
                    encodedCode +
                    "</pre></div>"
                );
            },
        })
        : null;

function MarkdownRenderer({ content }) {
    if (!content) return null;
    const cleaned = cleanContent(content);
    if (!cleaned) return null;

    const obgenStyle = `
        /* --- HEADINGS --- */
        .obgenai-markdown h1 { font-size: 1.5rem !important; font-weight: 700; margin: 1rem 0 0.5rem; color: #e8e8e8; border-bottom: 1px solid #333; padding-bottom: 0.3rem; }
        .obgenai-markdown h2 { font-size: 1.35rem !important; font-weight: 600; margin: 0.9rem 0 0.4rem; color: #e0e0e0; }
        .obgenai-markdown h3 { font-size: 1.15rem !important; font-weight: 600; margin: 0.8rem 0 0.3rem; color: #d0d0d0; }
        .obgenai-markdown h4 { font-size: 1rem !important; font-weight: 600; margin: 0.6rem 0 0.2rem; color: #c0c0c0; }

        /* --- PARAGRAPHS & TEXT --- */
        .obgenai-markdown p { margin: 0.4rem 0; line-height: 1.65; color: #d4d4d4; }
        .obgenai-markdown strong { color: #f0f0f0; font-weight: 600; }

        /* --- INLINE CODE --- */
        .obgenai-markdown code:not(pre code) {
            background: rgba(255, 255, 255, 0.08);
            color: #e06c75;
            padding: 2px 6px;
            border-radius: 4px;
            font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
            font-size: 0.88em;
        }

        /* --- CODE BLOCKS --- */
        .obgenai-markdown pre {
            background: #1a1a2e !important;
            border: 1px solid #2a2a3e;
            border-radius: 8px;
            padding: 14px 16px;
            margin: 0.6rem 0;
            overflow-x: auto;
            font-size: 0.85rem;
            line-height: 1.5;
        }
        .obgenai-markdown pre code {
            background: none !important;
            color: #e8e8e8;
            padding: 0;
            font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
        }

        /* --- LISTS --- */
        .obgenai-markdown ul, .obgenai-markdown ol {
            margin: 0.4rem 0;
            padding-left: 1.5rem;
            color: #d4d4d4;
        }
        .obgenai-markdown li { margin: 0.2rem 0; line-height: 1.6; }
        .obgenai-markdown li > p { margin: 0.15rem 0; }

        /* --- BLOCKQUOTES --- */
        .obgenai-markdown blockquote {
            border-left: 3px solid #8dcff8;
            background: rgba(141, 207, 248, 0.05);
            margin: 0.5rem 0;
            padding: 8px 14px;
            border-radius: 0 6px 6px 0;
            color: #b8b8b8;
            font-style: italic;
        }
        .obgenai-markdown blockquote p { margin: 0.2rem 0; }

        /* --- TABLES --- */
        .obgenai-markdown table {
            width: 100%;
            border-collapse: collapse;
            margin: 0.6rem 0;
            font-size: 0.9rem;
        }
        .obgenai-markdown th {
            background: rgba(255, 255, 255, 0.06);
            color: #e0e0e0;
            font-weight: 600;
            padding: 8px 12px;
            text-align: left;
            border-bottom: 2px solid #333;
        }
        .obgenai-markdown td {
            padding: 6px 12px;
            border-bottom: 1px solid #2a2a2a;
            color: #c8c8c8;
        }
        .obgenai-markdown tr:hover td { background: rgba(255, 255, 255, 0.03); }

        /* --- LINKS --- */
        .obgenai-markdown a {
            color: #8dcff8;
            text-decoration: none;
            border-bottom: 1px solid transparent;
            transition: border-color 0.2s;
        }
        .obgenai-markdown a:hover { border-bottom-color: #8dcff8; }

        /* --- HORIZONTAL RULES --- */
        .obgenai-markdown hr {
            border: none;
            border-top: 1px solid #333;
            margin: 1rem 0;
        }

        /* --- IMAGES --- */
        .obgenai-markdown img { max-width: 100%; border-radius: 6px; margin: 0.5rem 0; }
    `;

    if (markdown) {
        let htmlContent = "";
        try {
            htmlContent = markdown.render(cleaned, "javascript");
        } catch (e) {
            console.error("Markdown rendering error:", e);
        }
        return (
            <>
                <style>{obgenStyle}</style>
                <div
                    className="obgenai-markdown"
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
            </>
        );
    }

    return (
        <>
            <style>{obgenStyle}</style>
            <div className="obgenai-markdown">{cleaned}</div>
        </>
    );
}
/**
 * SummarizationBubble component
 * @param summary - The summarization event
 * @returns The SummarizationBubble component
 */
const SummarizationBubble = ({ summary }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    return (
        <div
            style={{ display: "flex", justifyContent: "center", margin: "16px 0" }}
        >
            <div
                style={{
                    backgroundColor: "#1f2937",
                    border: "1px solid #374151",
                    borderRadius: "8px",
                    padding: "16px",
                    maxWidth: "600px",
                    width: "100%",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: "12px",
                    }}
                >
                    <div style={{ flex: 1 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "8px",
                            }}
                        >
                            <svg
                                style={{
                                    width: "20px",
                                    height: "20px",
                                    color: "#60a5fa",
                                    flexShrink: 0,
                                }}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                            <span
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#bfdbfe",
                                }}
                            >
                                Summarization Middleware Activated
                            </span>
                            <span
                                style={{
                                    fontSize: "12px",
                                    color: "#93c5fd",
                                    backgroundColor: "rgba(30, 58, 138, 0.5)",
                                    padding: "2px 8px",
                                    borderRadius: "12px",
                                }}
                            >
                                Previous conversation summarized
                            </span>
                        </div>
                        {summary.isStreaming ? (
                            <div
                                style={{
                                    fontSize: "14px",
                                    color: "#e0e7ff",
                                    backgroundColor: "rgba(17, 24, 39, 0.5)",
                                    borderRadius: "4px",
                                    padding: "12px",
                                    border: "1px solid #374151",
                                    marginTop: "8px",
                                }}
                            >
                                <pre
                                    style={{
                                        whiteSpace: "pre-wrap",
                                        fontFamily: "monospace",
                                        fontSize: "12px",
                                        overflowX: "auto",
                                        margin: 0,
                                        backgroundColor: "transparent",
                                    }}
                                >
                                    {summary.summary}
                                    <Spin size="small" style={{ marginLeft: "4px" }} />
                                </pre>
                            </div>
                        ) : (
                            <>
                                <button
                                    onClick={() => setIsExpanded(!isExpanded)}
                                    style={{
                                        fontSize: "12px",
                                        color: "#60a5fa",
                                        backgroundColor: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        fontWeight: "500",
                                        marginBottom: "8px",
                                        padding: 0,
                                    }}
                                >
                                    {isExpanded ? "Hide summary" : "Show summary"}
                                </button>
                                {isExpanded && (
                                    <div
                                        style={{
                                            fontSize: "14px",
                                            color: "#e0e7ff",
                                            backgroundColor: "rgba(17, 24, 39, 0.5)",
                                            borderRadius: "4px",
                                            padding: "12px",
                                            border: "1px solid #374151",
                                            marginTop: "8px",
                                        }}
                                    >
                                        <pre
                                            style={{
                                                whiteSpace: "pre-wrap",
                                                fontFamily: "monospace",
                                                fontSize: "12px",
                                                overflowX: "auto",
                                                margin: 0,
                                                backgroundColor: "transparent",
                                            }}
                                        >
                                            {summary.summary}
                                        </pre>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
function parseSummarizationEvent(data, currentMessageCount, onSummarization) {
    if (
        data &&
        typeof data === "object" &&
        "SummarizationMiddleware.before_model" in data
    ) {
        const summarizationData = data["SummarizationMiddleware.before_model"];
        if (
            !summarizationData ||
            typeof summarizationData !== "object" ||
            !("messages" in summarizationData)
        ) {
            return;
        }
        const messages = summarizationData.messages;
        if (!Array.isArray(messages) || messages.length < 2) {
            return;
        }
        // Get the second message (index 1) which contains the summary
        const summaryMessage = messages[1];
        if (!summaryMessage || typeof summaryMessage !== "object") {
            return;
        }
        const msgAny = summaryMessage;
        if (!("content" in msgAny)) {
            return;
        }
        let content;
        // Handle string content
        if (typeof msgAny.content === "string") {
            content = msgAny.content;
        }
        // Handle array content (extract text from content blocks)
        else if (Array.isArray(msgAny.content)) {
            const textParts = [];
            for (const item of msgAny.content) {
                if (!item) {
                    continue;
                }
                if (typeof item === "object") {
                    // Handle text content blocks: {type: "text", text: "..."}
                    if (
                        "type" in item &&
                        item.type === "text" &&
                        "text" in item &&
                        typeof item.text === "string"
                    ) {
                        textParts.push(item.text);
                    }
                } else if (typeof item === "string") {
                    // Handle direct string content in array
                    textParts.push(item);
                }
            }
            if (textParts.length > 0) {
                content = textParts.join("");
            }
        }
        // Check if content starts with the expected summary prefix
        if (
            !content ||
            !content.startsWith("Here is a summary of the conversation to date:")
        ) {
            return;
        }
        // Extract summary content (remove the prefix)
        const summary = content
            .replace(/^Here is a summary of the conversation to date:\s*/i, "")
            .trim();
        if (summary) {
            onSummarization({
                id: `summarization - ${Date.now()} `,
                timestamp: Date.now(),
                summary: summary,
                afterMessageIndex: Math.max(0, currentMessageCount - 1), // After the last message
            });
        }
    }
}
function ErrorBubble({ error }) {
    /**
     * ignore unknown errors
     */
    if (error.includes("OOM command not allowed")) {
        return;
    }
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "flex-start",
                marginTop: "16px",
            }}
        >
            <div
                style={{
                    maxWidth: "80%",
                    borderRadius: "8px",
                    border: "2px solid #ef4444",
                    backgroundColor: "rgba(127, 29, 29, 0.2)",
                    padding: "16px",
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {/* Header */}
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <svg
                            style={{
                                width: "20px",
                                height: "20px",
                                color: "#f87171",
                                flexShrink: 0,
                            }}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <span
                            style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "#fca5a5",
                                textTransform: "uppercase",
                            }}
                        >
                            Error
                        </span>
                    </div>

                    {/* Error Message */}
                    <div
                        style={{
                            backgroundColor: "#1f2937",
                            borderRadius: "8px",
                            padding: "12px",
                            border: "1px solid #7f1d1d",
                        }}
                    >
                        <p
                            style={{
                                fontSize: "14px",
                                color: "#fecaca",
                                whiteSpace: "pre-wrap",
                                margin: 0,
                            }}
                        >
                            {error.includes("thread limit exceeded")
                                ? "You have reached the limit of SMS credits. Please purchase more credits to continue."
                                : error.includes("run limit exceeded")
                                    ? "You can't send more than 2 SMS messages per session, upgrade your plan to send more messages."
                                    : error.includes("Model call limits exceeded")
                                        ? "It seems like I am having a hard time answering your question. Please contact our support team at (415) 555-1234 or write them an email at support@company.com."
                                        : error}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
function isAIMessageChunk(chunk) {
    if (!chunk || typeof chunk !== "object") return false;
    const chunkAny = chunk;
    // Check for LangGraph native format (type: "ai")
    if (chunkAny.type === "ai") return true;
    // Check for old format (lc and id array)
    return Boolean(
        "lc" in chunkAny &&
        "kwargs" in chunkAny &&
        "id" in chunkAny &&
        Array.isArray(chunkAny.id) &&
        chunkAny.id[2] === "AIMessageChunk",
    );
}
function isToolMessage(chunk) {
    if (!chunk || typeof chunk !== "object") return false;
    const chunkAny = chunk;
    // Check for LangGraph native format (type: "tool")
    if (chunkAny.type === "tool") return true;
    // Check for old format (lc and id array)
    return Boolean(
        "lc" in chunkAny &&
        "kwargs" in chunkAny &&
        "id" in chunkAny &&
        Array.isArray(chunkAny.id) &&
        chunkAny.id[2] === "ToolMessage",
    );
}

function ChatInterfaceInner({
    apiKey = "",
    collection,
    recordId,
    renderingSource,
    selectedApp,
    entryComponent,
    chatWindowHeight,
    handleAiAssist,
}) {
    const selectedScenario = "obto-agent";
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [toolCalls, setToolCalls] = useState(new Map());
    const [interruptData, setInterruptData] = useState(null);
    const [currentThreadId, setCurrentThreadId] = useState(undefined);
    const [summarizations, setSummarizations] = useState([]);
    const [errors, setErrors] = useState(new Map()); // Map of AI message ID to error message
    const [modelNames, setModelNames] = useState(new Map()); // Map of AI message ID to model name
    const textareaRef = useRef(null);
    const buttonRef = useRef(null);
    const accumulatedContentRef = useRef("");
    const messageCountRef = useRef(0);
    const processedTokenMessagesRef = useRef(new Set());
    const processedToolCallIdsRef = useRef(new Set());
    const summarizationContentRef = useRef(""); // Track accumulating summarization content
    const currentSummarizationIdRef = useRef(null); // Track current summarization ID
    const hasCreatedAIMessageRef = useRef(false); // Track if we've created any AI message during this stream
    const abortControllerRef = useRef(null);
    const [thinkingPhrase, setThinkingPhrase] = useState("Thinking");
    const [collapsed, setCollapsed] = useState(true);
    const [history, setHistory] = useState([]);
    const [isHover, setHover] = useState({});
    const [open, setOpen] = useState(false);
    const [historyPopoverOpen, setHistoryPopoverOpen] = useState(false);

    // New Footer State
    const [fileList, setFileList] = useState([]);
    const [isSpeechActive, setIsSpeechActive] = useState(false);
    const [pastedFileName, setPastedFileName] = useState(null);
    const [originalPastedText, setOriginalPastedText] = useState("");
    const [isPastePreviewOpen, setIsPastePreviewOpen] = useState(false);
    const [showSaveSpinner, setShowSaveSpinner] = useState(false);

    // Extracted dynamic context
    const [activeCollection, setActiveCollection] = useState(collection);
    const [activeRecordId, setActiveRecordId] = useState(recordId);
    const [activeApp, setActiveApp] = useState(selectedApp);
    const [domain] = useState("staging");
    const [userName] = useState("ai.admin");
    const [selectedFolder, setSelectedFolder] = useState("general");
    const [fileProgress, setFileProgress] = useState({});

    useEffect(() => {
        const pathSegments = window.location.pathname.split("/").filter(Boolean);
        const searchParams = new URLSearchParams(window.location.search);

        if (!collection && pathSegments.length >= 2) {
            setActiveCollection(pathSegments[0]);
        }
        if (!recordId && pathSegments.length >= 2) {
            setActiveRecordId(pathSegments[1]);
        }
        if (!selectedApp && searchParams.has("pagename")) {
            setActiveApp(searchParams.get("pagename"));
        }
    }, [collection, recordId, selectedApp]);

    const showPastePreview = () => setIsPastePreviewOpen(true);
    const hidePastePreview = () => setIsPastePreviewOpen(false);

    const generateUniqueId = (length = 10) => {
        const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let uniqueId = "";
        for (let i = 0; i < length; i++) {
            uniqueId += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return uniqueId;
    };

    const setFileName = (filename) => {
        const regex = /[()\/#\s'"\[\]\{\}:=;+&%^@,\-\\]/g;
        const userNameRegex = /[()\/#\s'"\[\]\{\}:=.;+&%^@,\-\\]/g;
        return `${generateUniqueId()}_${domain}_${selectedFolder}_${userName.replace(userNameRegex, "")}_${filename.replace(regex, "")}`;
    };

    const handleFile = (fileInfo) => {
        let fileUrlPrefix = "";
        const file = fileInfo.file;
        const tempFileList = [...fileList, file];
        const fileName = setFileName(file.name);
        const mixedfile = file.type.split("/");
        const filetype = mixedfile[0];
        if (filetype === "image") {
            fileUrlPrefix = `https://${domain}.obto.co/ms/filereader.bto?n=`;
        }
        if (filetype === "video") {
            fileUrlPrefix = `https://${domain}.obto.co/ms/videoplayer.bto?n=`;
        }
        if (filetype === "application" && typeof mixedfile[1] !== "undefined") {
            fileUrlPrefix = `https://${domain}.obto.co/ms/filereader.bto?n=`;
        }
        const fullFileUrl = `${fileUrlPrefix}${fileName}`;
        const TOKEN = localStorage.getItem("wsToken");
        setFileList([...fileList, file]);

        // Ensure tus is available, otherwise alert
        if (typeof tus === 'undefined') {
            const reader = new FileReader();
            reader.onload = (readEvent) => {
                setOriginalPastedText(readEvent.target.result);
                setPastedFileName(file.name);
            };
            reader.readAsText(file);
            return;
        }

        const upload = new tus.Upload(file, {
            endpoint: "https://fileupload.obto.workers.dev/",
            chunkSize: 100 * 1024 * 1024,
            uploadSize: file.size,
            parallelUploads: 1,
            headers: {
                Filename: fileName,
                source: selectedFolder,
                SourceDomain: "staging.obto.co",
                "Access-Control-Allow-Headers": "Upload-Offset",
                "Access-Control-Expose-Headers": "Upload-Offset",
                "Access-Control-Allow-Origin": "*",
                Authorization: "Bearer " + TOKEN,
            },
            retryDelays: [0, 3000, 5000, 15000, 20000],
            metadata: {
                filename: fileName,
                filetype: file.type,
                filesize: file.size,
                source: selectedFolder,
                collection: "notification",
                document: "global",
            },
            onError: (error) => console.log("Failed because: " + JSON.stringify(error)),
            onProgress: (bytesUploaded, bytesTotal) => {
                const percentage = (bytesUploaded / bytesTotal).toFixed(2);
                let tempFileProgress = { ...fileProgress };
                tempFileProgress[file.name] = percentage;
                setFileProgress(tempFileProgress);
            },
            onSuccess: () => {
                console.log(`Upload finished: ${fileName}`, fullFileUrl);
            },
        });
        upload.findPreviousUploads().then(function (previousUploads) {
            if (previousUploads.length) upload.resumeFromPreviousUpload(previousUploads[0]);
            upload.start();
        });
    };

    const handlePaste = (e) => {
        const pastedText = e.clipboardData.getData("text");
        const lineCount = pastedText.split("\n").length;

        if (lineCount > 15) {
            // Prevent the default paste into textarea
            e.preventDefault();
            // Store large pasted text separately
            setOriginalPastedText(pastedText);
            setPastedFileName(`Pasted Text (${lineCount} lines)`);
        }
        // If less than 15 lines, let default paste behavior occur
    };

    const getFooterStyle = () => {
        return {
            flex: "0 0 auto", // Do not grow, do not shrink. Size to content.
            padding: "10px 10px", // Padding for the input bar
            backgroundColor: "#111", // Opaque background so text doesn't scroll "under" transparently
            borderTop: "1px solid #333", // Optional: Visual separation
            zIndex: 10,
        };
    };
    const SIDEBAR_BUTTON_STYLE = {
        width: "100%",
        textAlign: "left",
        padding: "8px 12px",
        color: "#fff",
        height: "auto",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    };

    const menuScrollStyle = {
        "& .ant-menu-submenu-popup .ant-menu": {
            maxHeight: "300px",
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#404040 #1f1f1f",
        },
        "& .ant-menu-submenu-popup .ant-menu::-webkit-scrollbar": {
            width: "6px",
        },
        "& .ant-menu-submenu-popup .ant-menu::-webkit-scrollbar-track": {
            background: "#1f1f1f",
        },
        "& .ant-menu-submenu-popup .ant-menu::-webkit-scrollbar-thumb": {
            background: "#404040",
            borderRadius: "3px",
        },
        "& .ant-menu-submenu-popup .ant-menu::-webkit-scrollbar-thumb:hover": {
            background: "#505050",
        },
    };

    const {
        recordToolCall,
        recordModelCall,
        recordTokens,
        recordContextWindowSize,
        resetStatistics,
    } = useStatistics();

    // History Persistence Hooks
    const addToChatHistory = useCallback((messageContent) => {
        if (!currentThreadId) return;
        let chatHistory = localStorage.getItem("chatHistory");
        chatHistory = chatHistory ? JSON.parse(chatHistory) : {};
        chatHistory[currentThreadId] = {
            timestamp: Date.now(),
            text: messageContent,
            messages: messages, // Note: This captures current messages state
        };
        localStorage.setItem("chatHistory", JSON.stringify(chatHistory));

        let localStorageHistory = Object.keys(chatHistory).map((chatSessionid) => ({
            sessionId: chatSessionid,
            name: chatHistory[chatSessionid].text,
            timestamp: chatHistory[chatSessionid].timestamp || Date.now(),
        }));
        setHistory(localStorageHistory.sort((a, b) => b.timestamp - a.timestamp));
    }, [currentThreadId, messages]);

    useEffect(() => {
        const initialThreadId = generateUniqueId();
        setCurrentThreadId(initialThreadId);
        let chatHistory = localStorage.getItem("chatHistory");
        if (chatHistory) {
            chatHistory = JSON.parse(chatHistory);
            let localStorageHistory = Object.keys(chatHistory).map((chatSessionid) => ({
                sessionId: chatSessionid,
                name: chatHistory[chatSessionid].text,
                timestamp: chatHistory[chatSessionid].timestamp || Date.now(),
            }));
            setHistory(localStorageHistory.sort((a, b) => b.timestamp - a.timestamp));
        } else {
            setHistory([]);
        }
    }, []);

    useEffect(() => {
        if (messages.length > 0) {
            const firstUserMessage = messages.find(m => HumanMessage.isInstance(m)) || messages[0];
            if (firstUserMessage && firstUserMessage.content) {
                addToChatHistory(firstUserMessage.content);
            }
        }
    }, [messages, addToChatHistory]);

    // Tailwind removed to fix platform CSS conflict
    const handleNewChat = () => {
        setCurrentThreadId(generateUniqueId());
        setMessages([]);
        setToolCalls(new Map());
        setErrors(new Map());
        setModelNames(new Map());
        setInputValue("");
        setSummarizations([]);
        setInterruptData(null);
    };

    const deleteChat = (event, sessionId) => {
        event.stopPropagation();
        let chatHistory = localStorage.getItem("chatHistory");
        if (chatHistory) {
            chatHistory = JSON.parse(chatHistory);
            delete chatHistory[sessionId];
        }
        let localStorageHistory = Object.keys(chatHistory).map((chatSessionid) => ({
            sessionId: chatSessionid,
            name: chatHistory[chatSessionid].text,
            timestamp: chatHistory[chatSessionid].timestamp || Date.now(),
        }));
        setHistory(localStorageHistory);
        localStorage.setItem("chatHistory", JSON.stringify(chatHistory));
    };

    const showDeleteIcon = (sessionId) => {
        const tempIsHover = { ...isHover };
        tempIsHover[sessionId] = true;
        setHover(tempIsHover);
    };

    const hideDeleteIcon = (sessionId) => {
        const tempIsHover = { ...isHover };
        tempIsHover[sessionId] = false;
        setHover(tempIsHover);
    };

    const setPromptHistory = (sessionId) => {
        setCurrentThreadId(sessionId);
        let chatHistory = localStorage.getItem("chatHistory");
        if (chatHistory) {
            chatHistory = JSON.parse(chatHistory);
            const savedMessages = chatHistory[sessionId]?.messages || [];

            // Map old GenAIChatInterface message formats to Baseline formats
            const compatibleMessages = savedMessages.map((msg) => {
                if (msg.type === "question") {
                    return { type: "human", content: msg.text || "" };
                } else if (msg.type === "answer") {
                    return { type: "ai", content: msg.rawText || msg.text || "", id: msg.msgId };
                }
                return msg;
            });

            setMessages(compatibleMessages);
        }
    };

    const chatHistoryContent = (
        <div style={{ maxHeight: "350px", overflowY: "auto", width: "250px", scrollbarWidth: "thin", scrollbarColor: "#404040 #1f1f1f" }}>
            {history.length === 0 ? (
                <div style={{ padding: "12px", color: "#888", textAlign: "center" }}>No recent chats</div>
            ) : (
                history.map((item) => (
                    <Button
                        key={item.sessionId}
                        type="text"
                        style={{
                            ...SIDEBAR_BUTTON_STYLE,
                            flexDirection: "column",
                            alignItems: "flex-start",
                        }}
                        onMouseEnter={() => showDeleteIcon(item.sessionId)}
                        onMouseLeave={() => hideDeleteIcon(item.sessionId)}
                        onClick={() => {
                            setPromptHistory(item.sessionId);
                            setHistoryPopoverOpen(false);
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
                            {isHover[item.sessionId] ? (
                                <DeleteOutlined onClick={(event) => deleteChat(event, item.sessionId)} />
                            ) : (
                                <MessageOutlined />
                            )}
                            <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {item.name?.substring(0, 25)}
                            </span>
                        </div>
                        {item.timestamp && (
                            <div style={{ fontSize: "10px", color: "#999", marginTop: "2px", marginLeft: "24px", fontStyle: "italic" }}>
                                {moment(item.timestamp).fromNow()}
                            </div>
                        )}
                    </Button>
                ))
            )}
        </div>
    );

    // Reset state when scenario changes
    useEffect(() => {
        setMessages([]);
        setToolCalls(new Map());
        setInterruptData(null);
        setCurrentThreadId(undefined);
        setInputValue("");
        setSummarizations([]);
        setErrors(new Map());
        setModelNames(new Map());
        messageCountRef.current = 0;
        accumulatedContentRef.current = "";
        processedTokenMessagesRef.current = new Set();
        processedToolCallIdsRef.current = new Set();
        summarizationContentRef.current = "";
        currentSummarizationIdRef.current = null;
        hasCreatedAIMessageRef.current = false;
        resetStatistics();
    }, [selectedScenario, resetStatistics]);
    // Auto-resize textarea and sync button height
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "52px";
            const scrollHeight = textareaRef.current.scrollHeight;
            const newHeight = Math.min(scrollHeight, 200);
            textareaRef.current.style.height = `${newHeight} px`;
            // Sync button height to match textarea's actual height
            if (buttonRef.current) {
                const height = textareaRef.current.offsetHeight;
                buttonRef.current.style.height = `${height} px`;
            }
        }
    }, [inputValue]);

    // Cycle thinking phrases when active
    useEffect(() => {
        let interval;
        if (isLoading) {
            const phrases = [
                "Thinking",
                "Analyzing",
                "Deciphering",
                "Marinating",
                "Ideating",
                "Cooking",
                "Unraveling",
                "Doodling",
                "Unwrapping",
                "Processing",
                "Connecting dots",
            ];
            interval = setInterval(() => {
                setThinkingPhrase(phrases[Math.floor(Math.random() * phrases.length)]);
            }, 1000);
        } else {
            setThinkingPhrase("Thinking");
        }
        return () => clearInterval(interval);
    }, [isLoading]);

    const updateToolCallArgs = useCallback((modelRequest) => {
        // Handle both LangGraph native format (tool_calls directly on message) and our transformed format (kwargs.tool_calls)
        const messages = modelRequest.model_request.messages;
        let tc = [];
        for (const msg of messages) {
            // Check for LangGraph native format (tool_calls directly on message)
            const msgAny = msg;
            if (
                "tool_calls" in msgAny &&
                Array.isArray(msgAny.tool_calls) &&
                msgAny.tool_calls.length > 0
            ) {
                tc = msgAny.tool_calls;
                break;
            }
            // Check for our transformed format (kwargs.tool_calls)
            if (
                msg.kwargs?.tool_calls &&
                Array.isArray(msg.kwargs.tool_calls) &&
                msg.kwargs.tool_calls.length > 0
            ) {
                tc = msg.kwargs.tool_calls;
                break;
            }
        }
        setToolCalls((prevToolCalls) => {
            const newMap = new Map();
            for (const [id, toolCall] of prevToolCalls) {
                const updatedToolCall = tc.find((tc) => tc.id === id);
                newMap.set(
                    id,
                    updatedToolCall
                        ? {
                            ...toolCall,
                            toolCall: updatedToolCall,
                        }
                        : toolCall,
                );
            }
            return newMap;
        });
    }, []);
    const handleStop = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsLoading(false);
    }, []);

    const handleSend = useCallback(
        async (messageOverride, interruptResponse) => {
            const messageToSend = messageOverride || inputValue;
            // Allow sending if we have a message OR if we're resuming from an interrupt
            if (
                (!messageToSend.trim() && !interruptResponse && !originalPastedText && fileList.length === 0) ||
                !selectedScenario ||
                isLoading
            ) {
                return;
            }

            const finalMessageText = messageToSend.trim() + (originalPastedText ? "\n\n" + originalPastedText : "");

            // Only add user message if not resuming from interrupt
            if (!interruptResponse && (finalMessageText || fileList.length > 0)) {
                const userMessage = new HumanMessage(finalMessageText);
                userMessage.files = fileList;
                setMessages((prev) => {
                    const newMessages = [...prev, userMessage];
                    messageCountRef.current = newMessages.length;
                    return newMessages;
                });
                setInputValue("");
                setPastedFileName(null);
                setOriginalPastedText("");
                setFileList([]); // Clear uploaded files once sent
            }
            // Track current AI message ID - will be updated when we detect a new message
            let currentAssistantId = (Date.now() + 1).toString();
            const accumulatedContentByMessageRef = new Map();
            try {
                // Use the single endpoint directly
                const apiEndpoint = "/ms/streamllm.bto";
                // Generate or use existing thread ID
                const threadId = currentThreadId || `thread - ${Date.now()} `;
                if (!currentThreadId) {
                    setCurrentThreadId(threadId);
                }
                // Prepare AbortController
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                }
                abortControllerRef.current = new AbortController();

                // Send request to API
                const jwt = localStorage.getItem("wsToken");
                const response = await fetch(apiEndpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${jwt} `,
                    },
                    signal: abortControllerRef.current.signal,
                    body: JSON.stringify({
                        message: finalMessageText || messageToSend,
                        prompt: finalMessageText || messageToSend,
                        sessionId: threadId,
                        appName: activeApp || "pelatis_portal",
                        collection: activeCollection,
                        recordId: activeRecordId,
                        threadId,
                        interruptResponse: interruptResponse
                            ? JSON.stringify(interruptResponse)
                            : undefined,
                    }),
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(
                        `HTTP error! status: ${response.status}: ${errorText} `,
                    );
                }
                // Reset refs for this stream
                accumulatedContentRef.current = "";
                hasCreatedAIMessageRef.current = false;
                // Reset current assistant ID
                accumulatedContentByMessageRef.clear();
                // Helper function to update tool call state with tool message result
                const updateToolCallWithMessage = (toolMsg) => {
                    // Handle both LangGraph native format (tool_call_id directly) and old format (kwargs.tool_call_id)
                    const toolMsgAny = toolMsg;
                    const toolCallId =
                        toolMsgAny.tool_call_id || toolMsgAny.kwargs?.tool_call_id;
                    if (!toolCallId) {
                        return;
                    }
                    setToolCalls((prev) => {
                        const newMap = new Map(prev);
                        const existing = newMap.get(toolCallId);
                        // Extract name from both formats
                        const name =
                            toolMsgAny.name || toolMsgAny.kwargs?.name || "unknown";
                        if (existing) {
                            newMap.set(toolCallId, {
                                ...existing,
                                toolMessage: toolMsg,
                            });
                        } else {
                            // If we haven't seen the tool call yet, create a placeholder
                            newMap.set(toolCallId, {
                                toolCall: {
                                    id: toolCallId,
                                    name,
                                    args: {},
                                    type: "tool_call",
                                },
                                toolMessage: toolMsg,
                                aiMessageId: currentAssistantId,
                                timestamp: Date.now(),
                            });
                        }
                        return newMap;
                    });
                };
                // Helper function to process update events (streaming AIMessageChunk data or ToolMessage data)
                const processUpdate = (data) => {
                    /**
                     * Parse the summarization event from the update data (for updates event)
                     * Only parse if we're not already streaming a summarization (to avoid duplicates)
                     */
                    if (!currentSummarizationIdRef.current) {
                        parseSummarizationEvent(
                            data,
                            messageCountRef.current,
                            (summary) => {
                                setSummarizations((prev) => [...prev, summary]);
                            },
                        );
                    }
                    // Check if this is an array update (either [AIMessageChunk, LangGraphMetadata] or [ToolMessage, LangGraphMetadata])
                    if (!Array.isArray(data) || data.length !== 2) {
                        return;
                    }
                    // Check metadata for summarization middleware
                    const metadata = data[1];
                    const isSummarizationMessage =
                        metadata &&
                        typeof metadata === "object" &&
                        (("langgraph_node" in metadata &&
                            metadata.langgraph_node ===
                            "SummarizationMiddleware.before_model") ||
                            ("langgraph_triggers" in metadata &&
                                Array.isArray(metadata.langgraph_triggers) &&
                                metadata.langgraph_triggers.includes(
                                    "branch:to:SummarizationMiddleware.before_model",
                                )));
                    // Handle ToolMessage updates
                    if (isToolMessage(data[0])) {
                        const toolMsg = data[0];
                        updateToolCallWithMessage(toolMsg);
                        return;
                    }
                    // Handle AIMessageChunk updates
                    if (!isAIMessageChunk(data[0])) {
                        return;
                    }
                    const msg = data[0];
                    // Handle both LangGraph native format (id as string) and old format (kwargs.id)
                    const msgId = typeof msg.id === "string" ? msg.id : msg.kwargs?.id;
                    const messageId = msgId || currentAssistantId;
                    // Extract model name from metadata for model-fallback scenario
                    if (
                        selectedScenario === "model-fallback" &&
                        metadata &&
                        typeof metadata === "object"
                    ) {
                        const modelName = metadata.ls_model_name;
                        if (modelName && messageId) {
                            setModelNames((prev) => {
                                const newMap = new Map(prev);
                                newMap.set(messageId, modelName);
                                return newMap;
                            });
                        }
                    }
                    // If this is a summarization message, stream it to the summarization bubble instead
                    if (isSummarizationMessage) {
                        // Extract text content from the chunk
                        const chunkText = extractIncrementalTextFromChunk(msg);
                        if (chunkText !== null && chunkText !== "") {
                            // Initialize summarization if this is the first chunk
                            if (!currentSummarizationIdRef.current) {
                                const summarizationId = `summarization - ${Date.now()} `;
                                currentSummarizationIdRef.current = summarizationId;
                                summarizationContentRef.current = "";
                                // Create initial summarization event
                                setSummarizations((prev) => [
                                    ...prev,
                                    {
                                        id: summarizationId,
                                        timestamp: Date.now(),
                                        summary: "",
                                        afterMessageIndex: Math.max(0, messageCountRef.current - 1),
                                        isStreaming: true,
                                    },
                                ]);
                            }
                            // Accumulate content
                            const currentAccumulated = summarizationContentRef.current;
                            let updatedContent;
                            if (!currentAccumulated) {
                                updatedContent = chunkText;
                            } else if (
                                chunkText.startsWith(currentAccumulated) &&
                                chunkText.length > currentAccumulated.length
                            ) {
                                // Full replacement: chunk contains accumulated + new text
                                updatedContent = chunkText;
                            } else if (chunkText === currentAccumulated) {
                                // Duplicate chunk, skip
                                return;
                            } else {
                                // Incremental chunk: append new text to accumulated
                                updatedContent = currentAccumulated + chunkText;
                            }
                            // Only update if content actually changed
                            if (updatedContent !== currentAccumulated) {
                                // Remove the prefix if present
                                let displayContent = updatedContent;
                                if (
                                    displayContent.startsWith(
                                        "Here is a summary of the conversation to date:",
                                    )
                                ) {
                                    displayContent = displayContent
                                        .replace(
                                            /^Here is a summary of the conversation to date:\s*/i,
                                            "",
                                        )
                                        .trim();
                                }
                                summarizationContentRef.current = updatedContent;
                                // Update the summarization bubble with streaming content
                                setSummarizations((prev) =>
                                    prev.map((s) =>
                                        s.id === currentSummarizationIdRef.current
                                            ? { ...s, summary: displayContent, isStreaming: true }
                                            : s,
                                    ),
                                );
                            }
                        }
                        // Don't process summarization messages as regular AI messages
                        return;
                    }
                    // Check if this is a new AI message (different ID)
                    if (msgId && msgId !== currentAssistantId) {
                        currentAssistantId = msgId;
                        // Create a new AI message bubble if it doesn't exist
                        setMessages((prev) => {
                            const exists = prev.some((m) => m.id === msgId);
                            if (!exists) {
                                hasCreatedAIMessageRef.current = true;
                                const newMessages = [
                                    ...prev,
                                    new AIMessage({
                                        id: msgId,
                                        content: "",
                                    }),
                                ];
                                messageCountRef.current = newMessages.length;
                                return newMessages;
                            }
                            return prev;
                        });
                        // Initialize accumulated content for this message
                        if (!accumulatedContentByMessageRef.has(msgId)) {
                            accumulatedContentByMessageRef.set(msgId, "");
                        }
                    }
                    // Extract text content from the chunk
                    const chunkText = extractIncrementalTextFromChunk(msg);
                    if (chunkText !== null && chunkText !== "") {
                        const currentAccumulated =
                            accumulatedContentByMessageRef.get(messageId) || "";
                        // Determine if this is incremental (append) or full replacement
                        let updatedContent;
                        if (!currentAccumulated) {
                            updatedContent = chunkText;
                        } else if (chunkText === currentAccumulated) {
                            // Duplicate chunk, skip
                            return;
                        } else if (chunkText.startsWith(currentAccumulated)) {
                            // Full replacement: chunk is the entire message so far (e.g. from a 'values' stream event)
                            updatedContent = chunkText;
                        } else if (currentAccumulated.startsWith(chunkText)) {
                            // Out of order or older full replacement chunk, ignore
                            return;
                        } else {
                            // Incremental chunk: append new text to accumulated (e.g. from a 'messages' stream event)
                            updatedContent = currentAccumulated + chunkText;
                        }
                        // Only update if content actually changed
                        if (updatedContent !== currentAccumulated) {
                            accumulatedContentByMessageRef.set(messageId, updatedContent);
                            // Immediately update UI with the accumulated content (real-time streaming)
                            setMessages((prev) =>
                                prev.map((m) =>
                                    m.id === messageId
                                        ? new AIMessage({ ...m, content: updatedContent })
                                        : m,
                                ),
                            );
                        }
                    }
                    // Extract tool calls from the message
                    // Handle both LangGraph native format (tool_calls directly) and old format (kwargs.tool_calls)
                    const msgAny = msg;
                    const toolCallsFromMsg =
                        msgAny.tool_calls || msgAny.kwargs?.tool_calls || [];
                    const associatedMessageId = msgId || currentAssistantId;
                    for (const toolCall of toolCallsFromMsg) {
                        setToolCalls((prev) => {
                            const newMap = new Map(prev);
                            if (!newMap.has(toolCall.id)) {
                                newMap.set(toolCall.id, {
                                    toolCall,
                                    aiMessageId: associatedMessageId,
                                    timestamp: Date.now(),
                                });
                            }
                            return newMap;
                        });
                    }
                };
                setIsLoading(true);
                readStream(response, {
                    update: (data) => {
                        // Process update events which contain streaming AIMessageChunk data
                        processUpdate(data);
                    },
                    tools: (data) => {
                        const toolMessages = data.tools?.messages || [];
                        for (const toolMsg of toolMessages) {
                            updateToolCallWithMessage(toolMsg);
                        }
                    },
                    agent_state: (data) => {
                        // Track context window size from agent_state events (which come from values events)
                        const messages = data.messages || [];
                        if (messages.length > 0) {
                            const contextWindowTokens = countTokensApproximately(messages);
                            recordContextWindowSize(contextWindowTokens);
                        }
                    },
                    model_request: (data) => {
                        updateToolCallArgs(data);
                        recordModelCall();
                        // Track token usage and tool calls from model_request events (these contain final, accurate counts)
                        const messages = data.model_request?.messages || [];
                        // Count tokens in the messages array to track context window size
                        if (messages.length > 0) {
                            const contextWindowTokens = countTokensApproximately(messages);
                            recordContextWindowSize(contextWindowTokens);
                        }
                        for (const message of messages) {
                            // Track tokens - handle both LangGraph native format (usage_metadata directly) and transformed format (kwargs.usage_metadata)
                            const msgAny = message;
                            const usage =
                                msgAny.usage_metadata || message.kwargs?.usage_metadata;
                            if (usage) {
                                const input = usage.input_tokens || 0;
                                const output = usage.output_tokens || 0;
                                const total = usage.total_tokens || input + output;
                                // Only track if we have valid token counts (input_tokens > 0 indicates final count)
                                if (input > 0 && total > 0) {
                                    const messageId = msgAny.id || message.kwargs?.id;
                                    if (
                                        messageId &&
                                        !processedTokenMessagesRef.current.has(messageId)
                                    ) {
                                        recordTokens(input, output, total);
                                        processedTokenMessagesRef.current.add(messageId);
                                    }
                                }
                            }
                            // Track tool calls (only once per unique tool call ID across the conversation)
                            // Handle both LangGraph native format (tool_calls directly) and transformed format (kwargs.tool_calls)
                            const toolCalls = msgAny.tool_calls || message.kwargs?.tool_calls;
                            if (toolCalls && Array.isArray(toolCalls)) {
                                for (const toolCall of toolCalls) {
                                    if (
                                        toolCall &&
                                        typeof toolCall === "object" &&
                                        "id" in toolCall &&
                                        "name" in toolCall
                                    ) {
                                        const toolCallId = toolCall.id;
                                        if (!processedToolCallIdsRef.current.has(toolCallId)) {
                                            recordToolCall(toolCall.name);
                                            processedToolCallIdsRef.current.add(toolCallId);
                                        }
                                    }
                                }
                            }
                        }
                    },
                    interrupt: (data) => {
                        // Handle interrupt event - show bubble for user approval
                        if (
                            data &&
                            Array.isArray(data.actionRequests) &&
                            data.actionRequests.length > 0
                        ) {
                            setInterruptData(data);
                        }
                    },
                    end: () => {
                        setIsLoading(false);
                        // Ensure all content is displayed when stream ends
                        setMessages((prev) =>
                            prev.map((msg) => {
                                const msgAccumulated =
                                    accumulatedContentByMessageRef.get(msg.id || "") || "";
                                if (msgAccumulated && msgAccumulated !== msg.content) {
                                    return new AIMessage({ ...msg, content: msgAccumulated });
                                }
                                return msg;
                            }),
                        );
                        // Mark summarization as complete (no longer streaming)
                        if (currentSummarizationIdRef.current) {
                            setSummarizations((prev) =>
                                prev.map((s) =>
                                    s.id === currentSummarizationIdRef.current
                                        ? { ...s, isStreaming: false }
                                        : s,
                                ),
                            );
                            currentSummarizationIdRef.current = null;
                            summarizationContentRef.current = "";
                        }
                    },
                    error: (error) => {
                        setIsLoading(false);
                        if (error.name === "AbortError" || error.message.includes("abort")) {
                            setMessages((prev) => {
                                const exists = prev.some((m) => m.id === currentAssistantId);
                                if (exists) {
                                    return prev.map((m) =>
                                        m.id === currentAssistantId
                                            ? new AIMessage({ ...m, content: "[User interrupted the conversation]" })
                                            : m
                                    );
                                } else {
                                    return [
                                        ...prev,
                                        new AIMessage({
                                            id: currentAssistantId || `interrupt-${Date.now()}`,
                                            content: "[User interrupted the conversation]",
                                        }),
                                    ];
                                }
                            });
                            return;
                        }
                        const errorMessage = error.message || "Unknown error occurred";
                        // Check if we've created an AI message for this request
                        if (hasCreatedAIMessageRef.current && currentAssistantId) {
                            // Attach error to the AI message we created for this request
                            setErrors((prev) => {
                                const newMap = new Map(prev);
                                newMap.set(currentAssistantId, errorMessage);
                                return newMap;
                            });
                        } else {
                            // If there's no assistant message for this request yet, create one with empty content
                            // The error will be displayed via ErrorBubble component
                            // This handles cases where the error occurs before any AI message is created
                            const errorMsgId = `error - ${Date.now()} `;
                            const errorMsg = new AIMessage({
                                id: errorMsgId,
                                content: "",
                            });
                            // Find the index of the last human message to ensure error appears after it
                            setMessages((prev) => {
                                // Find the last index of a human message
                                let lastHumanIndex = -1;
                                for (let i = prev.length - 1; i >= 0; i--) {
                                    if (HumanMessage.isInstance(prev[i])) {
                                        lastHumanIndex = i;
                                        break;
                                    }
                                }
                                // If we found a human message, insert error after it
                                // Otherwise, append to the end
                                if (lastHumanIndex >= 0) {
                                    const newMessages = [...prev];
                                    newMessages.splice(lastHumanIndex + 1, 0, errorMsg);
                                    return newMessages;
                                } else {
                                    // No human message found, append to end
                                    return [...prev, errorMsg];
                                }
                            });
                            // Store error for the new message
                            setErrors((prev) => {
                                const newMap = new Map(prev);
                                newMap.set(errorMsgId, errorMessage);
                                return newMap;
                            });
                        }
                        // Mark all pending tool calls (those without toolMessage) as errored
                        setToolCalls((prev) => {
                            // Only create new Map if we have pending tool calls to mark as errored
                            let needsUpdate = false;
                            for (const toolCall of prev.values()) {
                                if (
                                    toolCall.aiMessageId === currentAssistantId &&
                                    !toolCall.toolMessage &&
                                    !toolCall.errored
                                ) {
                                    needsUpdate = true;
                                    break;
                                }
                            }
                            if (!needsUpdate) return prev;
                            const newMap = new Map(prev);
                            for (const [id, toolCall] of prev) {
                                if (
                                    toolCall.aiMessageId === currentAssistantId &&
                                    !toolCall.toolMessage
                                ) {
                                    newMap.set(id, { ...toolCall, errored: true });
                                }
                            }
                            return newMap;
                        });
                    },
                });
            } catch (error) {
                if (error.name === "AbortError") {
                    console.log("[BaselineChatInterface] User aborted fetch.");
                    return;
                }
                console.error("Error sending message:", error);
                setIsLoading(false);
                const errorMessage =
                    error instanceof Error ? error.message : "Failed to get response";
                // If we have a current assistant ID, store the error and mark pending tool calls as errored
                if (currentAssistantId) {
                    setErrors((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(currentAssistantId, errorMessage);
                        return newMap;
                    });
                    // Mark all pending tool calls as errored
                    setToolCalls((prev) => {
                        // Only create new Map if we have pending tool calls to mark as errored
                        let needsUpdate = false;
                        for (const toolCall of prev.values()) {
                            if (
                                toolCall.aiMessageId === currentAssistantId &&
                                !toolCall.toolMessage &&
                                !toolCall.errored
                            ) {
                                needsUpdate = true;
                                break;
                            }
                        }
                        if (!needsUpdate) return prev;
                        const newMap = new Map(prev);
                        for (const [id, toolCall] of prev) {
                            if (
                                toolCall.aiMessageId === currentAssistantId &&
                                !toolCall.toolMessage
                            ) {
                                newMap.set(id, { ...toolCall, errored: true });
                            }
                        }
                        return newMap;
                    });
                } else {
                    // If there's no assistant message yet, create one with empty content
                    // The error will be displayed via ErrorBubble component
                    // This handles cases where the error occurs before any response
                    const errorMsgId = `error - ${Date.now()} `;
                    const errorMsg = new AIMessage({
                        id: errorMsgId,
                        content: "",
                    });
                    setMessages((prev) => [...prev, errorMsg]);
                    setErrors((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(errorMsgId, errorMessage);
                        return newMap;
                    });
                }
            }
        },
        [
            inputValue,
            selectedScenario,
            isLoading,
            apiKey,
            currentThreadId,
            recordToolCall,
            recordModelCall,
            recordTokens,
            updateToolCallArgs,
            recordContextWindowSize,
        ],
    );
    const handleInterruptApprove = useCallback(async () => {
        if (!interruptData) return;
        const interruptResponse = {
            decisions: [
                {
                    type: "approve",
                },
            ],
        };
        setInterruptData(null);
        await handleSend("", interruptResponse);
    }, [interruptData, handleSend]);
    const handleInterruptReject = useCallback(
        async (message) => {
            if (!interruptData) return;
            const interruptResponse = {
                decisions: [
                    {
                        type: "reject",
                        ...(message ? { message } : {}),
                    },
                ],
            };
            setInterruptData(null);
            await handleSend("", interruptResponse);
        },
        [interruptData, handleSend],
    );
    const handleInterruptEdit = useCallback(
        async (editedArgs) => {
            if (!interruptData) return;
            const actionRequest = interruptData.actionRequests[0];
            const interruptResponse = {
                decisions: [
                    {
                        type: "edit",
                        editedAction: {
                            name: actionRequest.name,
                            args: editedArgs,
                        },
                    },
                ],
            };
            setInterruptData(null);
            await handleSend("", interruptResponse);
        },
        [interruptData, handleSend],
    );
    const handleKeyPress = useCallback(
        (e) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
            }
        },
        [handleSend],
    );
    // Memoize tool calls by message ID for efficient lookups
    const toolCallsByMessageId = useMemo(() => {
        const map = new Map();
        for (const toolCall of toolCalls.values()) {
            if (toolCall.aiMessageId) {
                const existing = map.get(toolCall.aiMessageId) || [];
                existing.push(toolCall);
                map.set(toolCall.aiMessageId, existing);
            }
        }
        // Sort each array by timestamp
        for (const [messageId, calls] of map.entries()) {
            map.set(
                messageId,
                calls.sort((a, b) => a.timestamp - b.timestamp),
            );
        }
        return map;
    }, [toolCalls]);
    // Memoize the combined messages and summarizations array
    const renderedItems = useMemo(() => {
        // Create a combined array of messages and summarizations
        const items = [];
        // Add all messages
        messages.forEach((msg, index) => {
            items.push({ type: "message", data: msg, index });
        });
        // Add summarizations at their correct positions
        summarizations.forEach((summ) => {
            // Insert summarization after the specified message index
            items.push({
                type: "summarization",
                data: summ,
                index: summ.afterMessageIndex + 0.5, // Use 0.5 to place between messages
            });
        });
        // Sort by index (messages have integer indices, summarizations have index + 0.5)
        items.sort((a, b) => a.index - b.index);
        return items;
    }, [messages, summarizations]);
    const isSplitterMode = renderingSource === "idetabs";
    const baseFontSize = isSplitterMode ? "13px" : "16px";

    const CHAT_SCROLL_CSS = `
      .genai-chat-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
      .genai-chat-scroll::-webkit-scrollbar-track { background: transparent; }
      .genai-chat-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 3px; }
      .genai-chat-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.35); }
    `;

    return (
        <Layout
            style={{ height: isSplitterMode ? "100vh" : "100%", backgroundColor: "#1e1e1e", color: "#ececec", fontSize: baseFontSize }}
        >
            <style>{CHAT_SCROLL_CSS}</style>
            {/* Header Area */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: isSplitterMode ? "4px 8px" : "16px 24px",
                    borderBottom: "1px solid #333",
                    backgroundColor: "#141414",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Typography.Text strong style={{ color: "#fff", fontSize: baseFontSize }}>
                        Assist
                    </Typography.Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: isSplitterMode ? "8px" : "16px" }}>
                    <Button
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={handleNewChat}
                        style={{ color: "#999", fontSize: baseFontSize }}
                        title="New Chat"
                    />
                    <Popover
                        content={chatHistoryContent}
                        title="Recent Chats"
                        trigger="click"
                        open={historyPopoverOpen}
                        onOpenChange={setHistoryPopoverOpen}
                        placement="bottomRight"
                        overlayInnerStyle={{ backgroundColor: "#1f1f1f", border: "1px solid #333" }}
                    >
                        <Button
                            type="text"
                            icon={<HistoryOutlined />}
                            style={{ color: "#999", fontSize: baseFontSize }}
                            title="Recent Chats"
                        />
                    </Popover>
                    {typeof SettingOutlined !== "undefined" && (
                        <SettingOutlined
                            style={{
                                color: "#999",
                                cursor: "pointer",
                                fontSize: baseFontSize,
                            }}
                            onClick={() => setOpen(true)}
                        />
                    )}
                    {!isSplitterMode && typeof CloseOutlined !== "undefined" && (
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={handleAiAssist}
                            style={{ color: "#999" }}
                        />
                    )}
                </div>
            </div>

            <div className="genai-chat-scroll" style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: isSplitterMode ? "12px 16px" : "24px 32px", scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.2) transparent" }}>
                {messages.length === 0 ? (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                        }}
                    >
                        <div style={{ textAlign: "center", color: "#888" }}>
                            <h3
                                style={{
                                    fontSize: isSplitterMode ? "15px" : "20px",
                                    fontWeight: "600",
                                    marginBottom: "8px",
                                    color: "#ececec",
                                }}
                            >
                                How can I help you today?
                            </h3>
                            <p style={{ fontSize: baseFontSize }}>Send a message to start chatting.</p>
                        </div>
                    </div>
                ) : (
                    <div
                        style={{
                            maxWidth: isSplitterMode ? "100%" : "800px",
                            margin: "0 auto",
                            display: "flex",
                            flexDirection: "column",
                            gap: isSplitterMode ? "16px" : "24px",
                        }}
                    >
                        {renderedItems.map((item) => {
                            if (item.type === "summarization") {
                                return (
                                    <SummarizationBubble key={item.data.id} summary={item.data} />
                                );
                            }
                            const message = item.data;
                            const messageIndex = Math.floor(item.index);
                            const associatedToolCalls =
                                AIMessage.isInstance(message) && message.id
                                    ? toolCallsByMessageId.get(message.id) || []
                                    : [];
                            const errorMessage = message.id
                                ? errors.get(message.id)
                                : undefined;
                            const modelName =
                                selectedScenario === "model-fallback" && message.id
                                    ? modelNames.get(message.id)
                                    : undefined;

                            const isHuman = HumanMessage.isInstance(message);

                            return (
                                <div key={message.id || messageIndex}>
                                    {message.content !== "" && (
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: isHuman ? "flex-end" : "flex-start",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    maxWidth: "90%",
                                                    borderRadius: "8px",
                                                    padding: "12px 16px",
                                                    backgroundColor: isHuman ? "#0e1012" : "#2d2d2d",
                                                    color: isHuman ? "#ffffff" : "#ececec",
                                                    fontSize: "13px",
                                                    lineHeight: "1.5",
                                                }}
                                            >
                                                {modelName && AIMessage.isInstance(message) && (
                                                    <div style={{ marginBottom: "8px" }}>
                                                        <span
                                                            style={{
                                                                display: "inline-block",
                                                                padding: "2px 8px",
                                                                fontSize: "12px",
                                                                borderRadius: "4px",
                                                                backgroundColor: "#3a3a3a",
                                                                color: "#ececec",
                                                            }}
                                                        >
                                                            {modelName}
                                                        </span>
                                                    </div>
                                                )}
                                                {AIMessage.isInstance(message) ? (
                                                    <div style={{ width: "100%" }}>
                                                        <MarkdownRenderer
                                                            content={String(message.content)}
                                                        />
                                                        {messageIndex === messages.length - 1 &&
                                                            isLoading && (
                                                                <Spin
                                                                    size="small"
                                                                    style={{ marginLeft: "8px" }}
                                                                />
                                                            )}
                                                    </div>
                                                ) : (
                                                    <div style={{ whiteSpace: "pre-wrap" }}>
                                                        {message.content}
                                                        {/*messageIndex === messages.length - 1 &&
                                                            isLoading && (
                                                                <Spin
                                                                    size="small"
                                                                    style={{ marginLeft: "8px" }}
                                                                />
                                                            )*/}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {associatedToolCalls.map((toolCallState) => (
                                        <ToolCallBubble
                                            key={toolCallState.toolCall.id}
                                            toolCallState={toolCallState}
                                        />
                                    ))}
                                    {errorMessage && AIMessage.isInstance(message) && (
                                        <ErrorBubble error={errorMessage} />
                                    )}
                                </div>
                            );
                        })}
                        {interruptData && (
                            <InterruptBubble
                                interruptData={interruptData}
                                onApprove={handleInterruptApprove}
                                onReject={handleInterruptReject}
                                onEdit={handleInterruptEdit}
                            />
                        )}
                        {!interruptData && isLoading && (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    padding: "8px",
                                }}
                            >
                                <Spin/>{"  "}{thinkingPhrase}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div style={getFooterStyle()}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "flex-end", // Align input/buttons to bottom
                        backgroundColor: "#1a1a1a",
                        padding: "10px",
                        borderRadius: "8px",
                    }}
                >
                    <Space>
                        <Upload
                            fileList={fileList}
                            onChange={handleFile}
                            beforeUpload={() => false}
                            multiple
                            showUploadList={false}
                            style={{ borderRadius: "50%" }}
                        >
                            <Button
                                type="text"
                                size="small"
                                style={{ color: "#666" }}
                                icon={<UploadOutlined />}
                            >
                                Upload
                            </Button>
                        </Upload>
                        <Button
                            type="text"
                            size="small"
                            style={{ color: "#666" }}
                            onClick={() => {
                                setMessages([]); // Assuming reset clears chat
                                setPastedFileName(null);
                                setOriginalPastedText("");
                                setFileList([]);
                            }}
                            icon={
                                <ReloadOutlined
                                    style={{
                                        color: "#fff",
                                        fontSize: "1em",
                                        cursor: "pointer",
                                    }}
                                />
                            }
                        />
                    </Space>

                    <div
                        style={{
                            flex: 1,
                            marginRight: "10px",
                            marginLeft: "10px",
                            position: "relative", // Added to contain absolute positioned element
                        }}
                    >
                        {pastedFileName && (
                            <>
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "-30px",
                                        right: "0",
                                        backgroundColor: "#2a2a2a",
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        zIndex: 1,
                                        cursor: "pointer",
                                    }}
                                >
                                    <FileTextOutlined style={{ color: "#666" }} />
                                    <span
                                        style={{ color: "#fff", fontSize: "12px" }}
                                        onClick={showPastePreview}
                                    >
                                        {pastedFileName}
                                    </span>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CloseOutlined />}
                                        style={{ marginLeft: "auto", color: "#666" }}
                                        onClick={() => {
                                            setPastedFileName(null);
                                            setOriginalPastedText("");
                                        }}
                                    />
                                </div>
                                <Modal
                                    title={pastedFileName}
                                    open={isPastePreviewOpen}
                                    onCancel={hidePastePreview}
                                    footer={[
                                        <Button key="close" onClick={hidePastePreview}>
                                            Close
                                        </Button>,
                                    ]}
                                    width={800}
                                    style={{ top: 20 }}
                                >
                                    <div
                                        style={{
                                            maxHeight: "70vh",
                                            overflow: "auto",
                                            backgroundColor: "#1a1a1a",
                                            padding: "16px",
                                            borderRadius: "4px",
                                            color: "#fff",
                                            fontFamily: "monospace",
                                            whiteSpace: "pre-wrap",
                                            wordWrap: "break-word",
                                        }}
                                    >
                                        {originalPastedText}
                                    </div>
                                </Modal>
                            </>
                        )}

                        {TextArea ? (
                            <TextArea
                                className="obgenai-textarea"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={isSpeechActive}
                                placeholder="Type your message..."
                                autoSize={{ minRows: 1, maxRows: 8 }}
                                style={{
                                    width: "100%",
                                    border: "none",
                                    backgroundColor: "transparent",
                                    color: "#fff",
                                    boxShadow: "none",
                                }}
                            />
                        ) : (
                            <textarea
                                className="obgenai-textarea"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={isSpeechActive}
                                placeholder="Type your message..."
                                rows={1}
                                style={{
                                    width: "100%",
                                    border: "none",
                                    backgroundColor: "transparent",
                                    color: "#fff",
                                    resize: "none",
                                    outline: "none",
                                    padding: "4px 0",
                                }}
                            />
                        )}
                    </div>
                    {typeof SpeechCapture !== "undefined" && (
                        <SpeechCapture
                            apiUrl="/transcribe.bto"
                            onResult={(t) => {
                                setInputValue((prev) => (prev ? prev + " " + t : t));
                            }}
                            useWebSocket={false}
                            maxDurationSec={120}
                            onStatusChange={(s) => setIsSpeechActive(s === "recording" || s === "uploading")}
                        />
                    )}
                    {isLoading && StopOutlined ? (
                        <Button
                            type="primary"
                            icon={<StopOutlined style={{ fontSize: "16px" }} />}
                            onClick={handleStop}
                            style={{
                                backgroundColor: "#3a3a3a",
                                color: "#ececec",
                                border: "none",
                                borderRadius: "50%",
                                width: "32px",
                                height: "32px",
                                minWidth: "32px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                marginLeft: "8px",
                            }}
                        />
                    ) : Button && ArrowUpOutlined ? (
                        <Button
                            type="primary"
                            icon={<ArrowUpOutlined style={{ fontSize: "16px" }} />}
                            onClick={() => handleSend()}
                            disabled={(!inputValue.trim() && fileList.length === 0) || isSpeechActive}
                            style={{
                                backgroundColor: inputValue.trim() || fileList.length > 0 ? "#ececec" : "#3a3a3a",
                                color: inputValue.trim() || fileList.length > 0 ? "#000" : "#888",
                                border: "none",
                                borderRadius: "50%",
                                width: "32px",
                                height: "32px",
                                minWidth: "32px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                marginLeft: "8px",
                            }}
                        />
                    ) : isLoading ? (
                        <button
                            onClick={handleStop}
                            style={{
                                backgroundColor: "#3a3a3a",
                                color: "#ececec",
                                border: "none",
                                borderRadius: "50%",
                                width: "32px",
                                height: "32px",
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                marginLeft: "8px",
                            }}
                        >
                            <StopOutlined style={{ fontSize: "16px" }} />
                        </button>
                    ) : (
                        <button
                            onClick={() => handleSend()}
                            disabled={(!inputValue.trim() && fileList.length === 0) || isSpeechActive}
                            style={{
                                backgroundColor: inputValue.trim() || fileList.length > 0 ? "#ececec" : "#3a3a3a",
                                color: inputValue.trim() || fileList.length > 0 ? "#000" : "#888",
                                border: "none",
                                borderRadius: "50%",
                                width: "32px",
                                height: "32px",
                                cursor: (inputValue.trim() || fileList.length > 0) && !isLoading ? "pointer" : "not-allowed",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                marginLeft: "8px",
                            }}
                        >
                            <ArrowUpOutlined style={{ fontSize: "16px" }} />
                        </button>
                    )}
                </div>
            </div>
        </Layout>
    );
}
/**
 * Reads a stream from the API and calls the callback function for each event.
 * @param response - The response from the API
 * @param callback - The callback function to handle the event
 * @returns void
 * @throws Error if the response body is not found or the event type is unknown
 * @throws Error if the event type is unknown
 * @throws Error if the data is not valid JSON
 */
async function readStream(response, callbacks) {
    try {
        // Handle SSE stream
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        if (!reader) {
            throw new Error("No response body");
        }
        let buffer = "";
        let currentEventType = null;
        let currentData = null;
        // Helper function to transform LangGraph's native event format into our expected format
        const transformLangGraphEvent = (langGraphEventType, payload) => {
            let eventType = "update";
            let eventData = payload;
            // Check for interrupts (human-in-the-loop pauses)
            if (langGraphEventType === "interrupt") {
                eventType = "interrupt";
                if (Array.isArray(payload) && payload.length > 0) {
                    const interruptValue = payload[0];
                    if (
                        interruptValue &&
                        typeof interruptValue === "object" &&
                        "value" in interruptValue
                    ) {
                        const value = interruptValue.value;
                        if (value?.actionRequests) {
                            eventData = {
                                actionRequests: value.actionRequests.map((ar) => ({
                                    name: ar.name,
                                    args: ar.args,
                                    description: ar.description,
                                })),
                                reviewConfigs: value.reviewConfigs || [],
                            };
                        } else {
                            eventData = interruptValue;
                        }
                    } else {
                        eventData = interruptValue;
                    }
                } else {
                    eventData = payload;
                }
            } else if (
                langGraphEventType === "values" &&
                typeof payload === "object" &&
                payload !== null
            ) {
                const values = payload;
                // Check for interrupt marker in values
                if (values.__interrupt__) {
                    eventType = "interrupt";
                    const interruptArray = values.__interrupt__;
                    // Extract action requests from interrupt
                    if (
                        interruptArray &&
                        interruptArray.length > 0 &&
                        interruptArray[0]?.value?.actionRequests
                    ) {
                        eventData = {
                            actionRequests: interruptArray[0].value.actionRequests.map(
                                (ar) => ({
                                    name: ar.name,
                                    args: ar.args,
                                    description: ar.description,
                                }),
                            ),
                            reviewConfigs: interruptArray[0].value.reviewConfigs || [],
                        };
                    } else {
                        eventData = values.__interrupt__;
                    }
                } else {
                    // Check data structure to determine event type
                    eventType = determineEventTypeFromPayload(payload);
                    eventData = payload;
                }
                // } else if (langGraphEventType === "updates" && typeof payload === "object" && payload !== null) {
                const updates = payload;
                // Check for interrupt marker in updates
                if (updates.__interrupt__) {
                    eventType = "interrupt";
                    const interruptArray = updates.__interrupt__;
                    // Extract action requests from interrupt
                    if (
                        interruptArray &&
                        interruptArray.length > 0 &&
                        interruptArray[0]?.value?.actionRequests
                    ) {
                        eventData = {
                            actionRequests: interruptArray[0].value.actionRequests.map(
                                (ar) => ({
                                    name: ar.name,
                                    args: ar.args,
                                    description: ar.description,
                                }),
                            ),
                            reviewConfigs: interruptArray[0].value.reviewConfigs || [],
                        };
                    } else {
                        eventData = updates.__interrupt__;
                    }
                } else {
                    // Check data structure to determine event type
                    eventType = determineEventTypeFromPayload(payload);
                    eventData = payload;
                }
                // } else if (langGraphEventType === "messages" && Array.isArray(payload)) {
                // Check if it's a chunk update (array of [AIMessageChunk, LangGraphMetadata])
                // LangGraph native format: [message, metadata] where message has type: "ai" or type: "tool"
                if (
                    payload.length === 2 &&
                    payload[0] &&
                    typeof payload[0] === "object" &&
                    payload[1] &&
                    typeof payload[1] === "object"
                ) {
                    const firstItem = payload[0];
                    const secondItem = payload[1];
                    // Check if second item is metadata (has langgraph_node)
                    if ("langgraph_node" in secondItem) {
                        // Check if first item is LangGraph native format message (has type property)
                        if (
                            "type" in firstItem &&
                            (firstItem.type === "ai" || firstItem.type === "tool")
                        ) {
                            // Convert to update format for processUpdate to handle
                            eventType = "update";
                            eventData = payload; // Keep as [message, metadata] for processUpdate
                        } else if ("lc" in firstItem && firstItem.lc === 1) {
                            // Old format with lc property
                            eventType = "model_request";
                            eventData = {
                                model_request: { messages: [payload[0]], _privateState: {} },
                            };
                        }
                    } else {
                        // Not a [message, metadata] pair, try to find AI messages
                        const aiMessages = payload.filter((msg) => {
                            if (!msg || typeof msg !== "object") return false;
                            const msgAny = msg;
                            // Check for LangGraph native format (type: "ai")
                            if (msgAny.type === "ai") return true;
                            // Check for old format (lc and id array)
                            return (
                                "lc" in msgAny &&
                                msgAny.lc === 1 &&
                                "id" in msgAny &&
                                Array.isArray(msgAny.id) &&
                                msgAny.id[0] === "langchain_core" &&
                                msgAny.id[1] === "messages" &&
                                (msgAny.id[2] === "AIMessageChunk" ||
                                    msgAny.id[2] === "AIMessage")
                            );
                        });
                        if (aiMessages.length > 0) {
                            eventType = "agent";
                            eventData = { messages: aiMessages };
                        }
                    }
                } else if (
                    payload.length > 0 &&
                    payload[0] &&
                    typeof payload[0] === "object"
                ) {
                    // Single message or array of messages - check for LangGraph native format
                    const firstMsg = payload[0];
                    if (firstMsg.type === "ai" || firstMsg.type === "tool") {
                        // Convert to update format - wrap single message with metadata if available
                        const metadata =
                            payload.length > 1 &&
                                typeof payload[1] === "object" &&
                                "langgraph_node" in payload[1]
                                ? payload[1]
                                : { langgraph_node: "unknown" };
                        eventType = "update";
                        eventData = [payload[0], metadata];
                    }
                }
            } else {
                // For other update types, check data structure
                if (typeof payload === "object" && payload !== null) {
                    eventType = determineEventTypeFromPayload(payload);
                    eventData = payload;
                }
            }
            return { eventType, eventData };
        };
        // Helper function to determine event type from payload structure
        const determineEventTypeFromPayload = (payload) => {
            if (typeof payload !== "object" || payload === null) {
                return "update";
            }
            const data = payload;
            const keys = Object.keys(data);
            // Check for agent_state: has "messages" key (and optionally "_privateState")
            if (keys.includes("messages") && Array.isArray(data.messages)) {
                return "agent_state";
            }
            // Check for model_request: single "model_request" key
            if (
                keys.length === 1 &&
                keys[0] === "model_request" &&
                data.model_request
            ) {
                return "model_request";
            }
            // Check for tools: single "tools" key
            if (keys.length === 1 && keys[0] === "tools" && data.tools) {
                return "tools";
            }
            // Check for chunk update: array of [AIMessageChunk, LangGraphMetadata]
            if (Array.isArray(payload) && payload.length === 2) {
                const first = payload[0];
                const second = payload[1];
                if (
                    typeof first === "object" &&
                    first !== null &&
                    "lc" in first &&
                    typeof second === "object" &&
                    second !== null &&
                    "langgraph_node" in second
                ) {
                    return "model_request";
                }
            }
            return "update";
        };
        // Helper function to handle different event types and call appropriate callbacks
        const handleEvent = (eventType, eventData) => {
            if (eventType === "end") {
                callbacks.end?.();
            } else if (eventType === "error") {
                // Extract error message - prefer 'message' field over 'error' field
                let errorMessage;
                if (typeof eventData === "string") {
                    errorMessage = eventData;
                } else if (eventData && typeof eventData === "object") {
                    const errorData = eventData;
                    errorMessage =
                        errorData.message || errorData.error || "Unknown error occurred";
                } else {
                    errorMessage = "Unknown error occurred";
                }
                callbacks.error?.(new Error(errorMessage));
            } else if (eventType === "agent_state") {
                callbacks.agent_state?.(eventData);
            } else if (eventType === "model_request") {
                callbacks.model_request?.(eventData);
            } else if (eventType === "tools") {
                callbacks.tools?.(eventData);
            } else if (eventType === "interrupt") {
                callbacks.interrupt?.(eventData);
            } else if (eventType === "update") {
                callbacks.update?.(eventData);
            } else if (eventType === "agent") {
                callbacks.agent?.(eventData);
            }
        };
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                // Process any remaining event in buffer before ending
                if (currentEventType !== null && currentData !== null) {
                    const isLangGraphEvent = !EVENT_TYPES.includes(currentEventType);
                    if (isLangGraphEvent) {
                        const { eventType, eventData } = transformLangGraphEvent(
                            currentEventType,
                            currentData,
                        );
                        currentEventType = eventType;
                        currentData = eventData;
                    }
                    const eventType = currentEventType;
                    handleEvent(eventType, currentData);
                }
                // Always call end callback when stream completes
                callbacks.end?.();
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";
            for (const line of lines) {
                if (!line.trim()) {
                    // Empty line signals end of event, process it
                    if (currentEventType !== null && currentData !== null) {
                        // Check if this is a LangGraph native event type or our custom format
                        const isLangGraphEvent = !EVENT_TYPES.includes(currentEventType);
                        if (isLangGraphEvent) {
                            // Transform LangGraph event format
                            const { eventType, eventData } = transformLangGraphEvent(
                                currentEventType,
                                currentData,
                            );
                            currentEventType = eventType;
                            currentData = eventData;
                        }
                        // Handle the transformed event
                        const eventType = currentEventType;
                        handleEvent(eventType, currentData);
                        // Reset for next event
                        currentEventType = null;
                        currentData = null;
                    }
                    continue;
                }
                if (line.startsWith("event: ")) {
                    currentEventType = line.slice(7).trim();
                    continue;
                }
                if (line.startsWith("data: ")) {
                    const dataStr = line.slice(6);
                    try {
                        currentData = JSON.parse(dataStr);
                    } catch (error) {
                        console.error("Error parsing stream data:", error);
                        callbacks.error?.(
                            error instanceof Error
                                ? error
                                : new Error("Failed to parse stream data"),
                        );
                    }
                    continue;
                }
            }
        }
        // Process any remaining event in buffer
        if (currentEventType !== null && currentData !== null) {
            const isLangGraphEvent = !EVENT_TYPES.includes(currentEventType);
            if (isLangGraphEvent) {
                const { eventType, eventData } = transformLangGraphEvent(
                    currentEventType,
                    currentData,
                );
                currentEventType = eventType;
                currentData = eventData;
            }
            const eventType = currentEventType;
            handleEvent(eventType, currentData);
        }
    } catch (error) {
        if (error.name === "AbortError" || error.message?.includes("abort")) {
            console.log("[BaselineChatInterface readStream] Stream reading aborted.");
            callbacks.error?.(error);
            return;
        }
        console.error("Error in readStream:", error);
        callbacks.error?.(error);
    }
}
// Helper function to extract incremental text from AIMessageChunk content array
// This extracts only the new text from content items of type "text"
// Handles both LangGraph native format (content directly on message) and old format (kwargs.content)
const extractIncrementalTextFromChunk = (msg) => {
    const msgAny = msg;
    // Handle LangGraph native format (content directly on message)
    let content = msgAny.content;
    // Fall back to old format (kwargs.content)
    if (!content && msgAny.kwargs && typeof msgAny.kwargs === "object") {
        content = msgAny.kwargs.content;
    }
    if (!content) {
        return null;
    }
    // If content is a string, return it
    if (typeof content === "string") {
        return content;
    }
    // If content is an array, extract text from text-type items
    if (Array.isArray(content)) {
        let text = "";
        for (const item of content) {
            if (item && typeof item === "object") {
                // Handle text content items: {"index": 0, "type": "text", "text": "I can"}
                if ("type" in item && item.type === "text" && "text" in item) {
                    text += String(item.text);
                }
                // Handle input_json_delta items (tool call arguments) - skip these for text display
                // These are: {"index": 1, "type": "input_json_delta", "input": "..."}
            }
        }
        return text || null;
    }
    return null;
};

function GenAIChatInterface(props) {
    return (
        <StatisticsProvider>
            <ChatInterfaceInner {...props} />
        </StatisticsProvider>
    );
}

export default GenAIChatInterface;